import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.ts';
import { useAuthStore, type CurrentUser } from './store.ts';

/**
 * Hydrates the auth store with the current user on mount. The caller (App)
 * MUST NOT render any auth-gated UI until `loading` flips to false — we
 * gate on `hydrated` (which only flips after the synchronous setUser call
 * has run), not on React Query's `isPending`. Otherwise the first render
 * after the fetch resolves briefly sees `user: null` in the store (because
 * setUser fires in an effect that runs after render) and any <RequireAuth>
 * in the tree pops the login modal even when the user is authenticated.
 */
export function useAuthBootstrap(): { loading: boolean } {
  const setUser = useAuthStore(s => s.setUser);
  const closeLoginModal = useAuthStore(s => s.closeLoginModal);
  const [hydrated, setHydrated] = useState(false);
  const { isPending, data, isError } = useQuery({
    queryKey: ['account'],
    queryFn: async () => {
      const res = await api.get<CurrentUser>('/api/account');
      if (res.status === 200) return res.data;
      if (res.status === 401) return null;
      throw new Error(`/api/account → ${res.status}`);
    },
    retry: false,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (isPending) return;
    const user = isError ? null : (data ?? null);
    setUser(user);
    // If we successfully recovered a session, also dismiss any login modal
    // that may have been opened by a 401 from a different in-flight request.
    if (user) closeLoginModal();
    setHydrated(true);
  }, [isPending, isError, data, setUser, closeLoginModal]);
  return { loading: !hydrated };
}
